import { createPalette } from './createPalette';
import { createTypography } from './createTypography';
import { createTransitions } from './createTransitions';
import { createBreakpoints } from './createBreakpoints';
import { createComponents } from './createComponents';
import { HoneycombThemeOverrides, HoneycombTheme, HoneycombThemeBase } from '../types/HoneycombTheme';
import { HoneycombInteraction } from '../types/HoneycombInteraction';
import { HoneycombZindex } from '../types/HoneycombZindex';
import {
    focusStyles,
    up,
    down,
    between,
    makeGutterStyles,
    makeContentMarginStyles,
    getColor,
    getTypographyVariant,
    getContrastTextColor,
    fontStyles,
    getSortedKeys,
    getSpacingValue,
    hoverStyles,
    resolveColorValue,
    resolveColorObject,
    isColorDark,
    resolveShadowValue,
} from '../utils';
import { alpha, deepMerge } from '../../utils';
import { PaletteActionFocus } from '../../design-tokens/honeycomb-tokens';
import { HoneycombRadii } from '../types/HoneycombRadii';
import { HoneycombShadows } from '../types/HoneycombShadows';
import { resolveRecursiveColorObject } from '../utils/resolveRecursiveColorObject';

const defaultInteraction: HoneycombInteraction = {
    disabledOpacity: 0.5,
    hoverShadow: `0 0 0 4px ${alpha(PaletteActionFocus, 0.1)}`,
    color: {
        disabled: 'grey.tint.300',
        scrollbar: 'grey.tint.300',
    },
    focusOutline: '2px solid var(--hc-color-action-focus)',
};

const defaultZindex: HoneycombZindex = {
    foreground: 2,
    dimmer: 10,
    sticky: 20,
    gallery: 25,
    toast: 30,
    popup: 100,
    modal: 110,
};

const defaultSpacingUnit = 4;

const defaultFocusRingStyles = {
    styles: (theme: HoneycombTheme) => ({
        outline: `2px solid ${theme.palette.action.focus}`,
    }),
};

const defaultShadows: HoneycombShadows = {
    none: 'none',
    xs: '0 -1px 2px rgba(0, 0, 0, 0.03), 0 1px 2px rgba(0, 0, 0, 0.05)',
    s: '0 -2px 4px rgba(0, 0, 0, 0.03), 0 4px 6px rgba(0, 0, 0, 0.06)',
    m: '0 -3px 6px rgba(0, 0, 0, 0.04), 0 6px 10px rgba(0, 0, 0, 0.08)',
    l: '0 -4px 8px rgba(0, 0, 0, 0.04), 0 10px 15px rgba(0, 0, 0, 0.08)',
    xl: '0 -5px 10px rgba(0, 0, 0, 0.05), 0 14px 22px rgba(0, 0, 0, 0.1)',
};

const defaultRadii: HoneycombRadii = {
    none: 'none',
    '2xs': '3px',
    xs: '6px',
    s: '10px',
    m: '20px',
    l: '28px',
};

export function attachFunctions(themeBase: HoneycombThemeBase): HoneycombTheme {
    return {
        ...themeBase,
        fn: {
            focusStyles: focusStyles(themeBase),
            fontStyles: fontStyles(themeBase),
            breakpoints: {
                up: up(themeBase),
                down: down(themeBase),
                between: between(themeBase),
                getSortedKeys: getSortedKeys(themeBase),
            },
            makeGutterStyles: makeGutterStyles(themeBase),
            makeContentMarginStyles: makeContentMarginStyles(themeBase),
            getColor: getColor(themeBase),
            resolveColorValue: resolveColorValue(themeBase),
            resolveFlatColorObject: resolveColorObject(themeBase),
            resolveRecursiveColorObject: resolveRecursiveColorObject(themeBase),
            resolveShadowValue: resolveShadowValue(themeBase),
            getContrastTextColor: getContrastTextColor(themeBase),
            getTypographyVariant: getTypographyVariant(themeBase),
            getSpacingValue: getSpacingValue(themeBase),
            isColorDark: isColorDark(themeBase),
            hoverStyles,
        },
    };
}

export function mergeThemeOverrides(
    currentTheme: HoneycombThemeBase,
    themeOverride?: HoneycombThemeOverrides
): HoneycombTheme {
    return attachFunctions(deepMerge(currentTheme, themeOverride));
}

export function createTheme(options?: HoneycombThemeOverrides): HoneycombTheme {
    const paletteOptions = options?.palette ?? {};
    const typographyOptions = options?.typography ?? {};
    const transitionOptions = options?.transitions ?? {};
    const breakpointOptions = options?.breakpoints ?? {};
    const interactionOptions = options?.interaction ?? {};
    const zIndexOptions = options?.zIndex ?? {};
    const componentsOptions = options?.components ?? {};
    const shadowsOptions = options?.shadows ?? {};
    const radiiOptions = options?.radii ?? {};

    const palette = createPalette(paletteOptions);
    const typography = createTypography(typographyOptions);
    const transitions = createTransitions(transitionOptions);
    const breakpoints = createBreakpoints(breakpointOptions);
    const components = createComponents(componentsOptions);

    const interaction = { ...defaultInteraction, ...interactionOptions };
    const zIndex = { ...defaultZindex, ...zIndexOptions };
    const shadows = { ...defaultShadows, ...shadowsOptions };
    const radii = { ...defaultRadii, ...radiiOptions };
    const spacingUnit = options?.spacingUnit ?? defaultSpacingUnit;
    const focusRingStyles = options?.focusRingStyles ?? defaultFocusRingStyles;

    const theme: HoneycombThemeBase = {
        palette,
        typography,
        transitions,
        interaction,
        breakpoints,
        zIndex,
        shadows,
        radii,
        spacingUnit,
        components,
        focusRingStyles,
    };

    return attachFunctions(theme);
}
